/**
 * authorityBadges — THE definition of "is this person council / admin".
 *
 * WHY THIS EXISTS (2026-08-20/21). "Council" was derived at least NINE times
 * across this app, and the derivations had drifted into three different answers.
 * The bug that surfaced it: `useBadges.isCouncil` checked only 'council-member'
 * (1 holder) while the real badge is 'council' (8 active holders), so 4 of 8
 * council members were locked out of Finance Hub — and `LotAccessGuard` had
 * ALREADY been fixed to accept both, so the lot guard said yes and the screen
 * inside it said no. Two gates on one door.
 *
 * Import these sets. Do NOT write badge-type string literals at a call site.
 *
 * ── The badge NAMES are a trap. Match on badge_type, never the display name ──
 *   badge_type       display name          definition        active holders
 *   'admin-access'   "🏛️ Council Member"   is_active=FALSE   8
 *   'super-admin'    "👑 Council Elder"    is_active=FALSE   3
 *   'council-member' "Council Member"      is_active=true    1
 *   'council'        "Council"             is_active=true    8
 *   'founder'        "Founder"             is_active=true    5
 *   'architect'      "Platform Architect"  is_active=FALSE   3
 *
 * The badge NAMED "Council Member" is badge_type 'admin-access', while a separate
 * 'council-member' badge_type also exists. Someone reaching for the name they saw
 * in the UI wrote 'council-member' and silently targeted the wrong thing — that is
 * the most likely origin of the whole bug.
 *
 * An inactive DEFINITION still grants: the badge reads filter on
 * badge_assignments.is_active and never join badge_definitions.is_active.
 *
 * ── Strings that DO NOT EXIST (verified 2026-08-21: no definition, no holders) ──
 * 'high-council', 'architect-council', 'architect-master', 'platform-owner'.
 * They appeared in five OR-chains, contributing nothing while looking like access.
 * An unheld badge in an OR chain is dead weight that reads as a grant.
 */

/** Council tier. Both strings are live; 'council' is the real one. */
export const COUNCIL_BADGES = Object.freeze(['council', 'council-member']);

/**
 * Admin tier. `founder` is here deliberately — a founder outranks admin, yet
 * before 2026-08-20 `founder` satisfied NO gate anywhere in the app and two of
 * the five founders were locked out of admin-level screens.
 */
export const ADMIN_BADGES = Object.freeze(['admin-access', 'super-admin', 'founder']);

/**
 * `architect` is a REAL badge (3 holders) and its own concept — platform
 * ownership, not council membership. Kept separate so nobody folds it into
 * COUNCIL_BADGES by accident, and so removing it from a gate is always a
 * deliberate, visible act.
 */
export const ARCHITECT_BADGES = Object.freeze(['architect']);

const includesAny = (badgeTypes, set) => {
  if (!Array.isArray(badgeTypes)) return false;
  return set.some((b) => badgeTypes.includes(b));
};

/** Council tier or better. Pass an array of badge_type strings. */
export const isCouncilBadge = (badgeTypes) => includesAny(badgeTypes, COUNCIL_BADGES);

/** Admin tier (includes founder). Pass an array of badge_type strings. */
export const isAdminBadge = (badgeTypes) => includesAny(badgeTypes, ADMIN_BADGES);

/** Platform architect. Pass an array of badge_type strings. */
export const isArchitectBadge = (badgeTypes) => includesAny(badgeTypes, ARCHITECT_BADGES);

/**
 * The common gate: council-or-above. Admin and founder outrank council, so a
 * screen asking "is this council?" for ACCESS almost always means this.
 */
export const hasCouncilAccess = (badgeTypes) =>
  isCouncilBadge(badgeTypes) || isAdminBadge(badgeTypes);
